import * as Sentry from '@sentry/react'
import to from 'await-to-js'
import { setCookie } from 'utils/cookies'
import { AxiosError, AxiosInstance } from 'axios'
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import posthog from 'posthog-js'
import { toast } from 'react-toastify'
import { User } from '../../models/user'
import {
  getClient,
  getToken,
  getUserType,
  removeToken,
  removeUserType,
  setToken,
  setUserType,
} from '../../utils/http-client'
import { useLeftNavStore } from '../../components/Layout/LeftNav/left-nav-store'
import { featureFlagService } from 'utils/featureFlagService'

// Analytics (PostHog, Sentry) enabled in non-dev environments, or when VITE_ENABLE_POSTHOG=true in .env.local
const isAnalyticsEnabled =
  import.meta.env.MODE !== 'development' ||
  import.meta.env.VITE_ENABLE_POSTHOG === 'true'

export const USER_TYPE_ADMIN = 'Admin' as const
export const USER_TYPE_STANDARD = 'Standard' as const
export const USER_TYPE_LIMITED = 'Limited' as const

export type UserType =
  | typeof USER_TYPE_ADMIN
  | typeof USER_TYPE_STANDARD
  | typeof USER_TYPE_LIMITED

const USER_TYPE_LEVEL: Record<UserType, number> = {
  [USER_TYPE_ADMIN]: 2,
  [USER_TYPE_STANDARD]: 1,
  [USER_TYPE_LIMITED]: 0,
}

const stub = (): never => {
  throw new Error('You forgot to wrap your component in <AuthProvider>.')
}

const initialContext: AuthContextInterface = {
  isAuthenticated: false,
  createUser: stub,
  login: stub,
  logout: stub,
  getClient: stub,
  isCheckingAuth: true,
  isVerifyStep: false,
  resetPassword: stub,
  resendVerify: stub,
  resetPasswordConfirm: stub,
  user: undefined,
  userType: USER_TYPE_STANDARD,
  isAdmin: false,
  isStandard: true,
  isLimited: false,
  isAllowed: () => false,
}

export const AuthContext = createContext<AuthContextInterface>(initialContext)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User>()
  const isAuthenticated = !!user
  const [isCheckingAuth, setIsCheckingAuth] = useState(true)
  const [isVerifyStep, setIsVerifyStep] = useState(false)
  const { enableUserTypes, isLoading: isFlagsLoading } = featureFlagService()

  // Read stored user type once on mount (set during login/tryAuth, survives page reload)
  const [storedUserType] = useState<UserType | null>(
    () => getUserType() as UserType | null
  )

  const userType: UserType = useMemo(() => {
    if (isFlagsLoading) {
      // Feature flags not loaded yet — use stored type from login/me response
      return storedUserType ?? USER_TYPE_STANDARD
    }
    if (!enableUserTypes) return USER_TYPE_ADMIN
    return (user?.user_type as UserType) ?? USER_TYPE_STANDARD
  }, [enableUserTypes, isFlagsLoading, user?.user_type, storedUserType])

  const isAdmin = userType === USER_TYPE_ADMIN
  const isStandard = userType === USER_TYPE_STANDARD
  const isLimited = userType === USER_TYPE_LIMITED

  const isAllowed = useCallback(
    (requiredType: UserType) => {
      if (!isFlagsLoading && !enableUserTypes) return true
      return USER_TYPE_LEVEL[userType] >= USER_TYPE_LEVEL[requiredType]
    },
    [enableUserTypes, isFlagsLoading, userType]
  )

  async function login(email: string, password: string) {
    const client = getClient({ reloadOnAuthError: false })

    try {
      const res = await client.post('auth/login/', {
        email: email,
        password: password,
      })
      // Store user type from login response for immediate use after page reload
      if (res.data.user?.user_type) {
        setUserType(res.data.user.user_type)
      }
      setToken(res.data.key)
    } catch (error) {
      throw error as AxiosError
    }
  }

  async function resetPasswordConfirm(
    uid: string,
    token: string,
    new_password1: string,
    new_password2: string
  ) {
    const client = getClient()

    try {
      const res = await client.post(`password-reset-confirm/`, {
        new_password1,
        new_password2,
        uid,
        token,
      })

      return res.data as {
        detail: string
      }
    } catch (error) {
      throw error as AxiosError
    }
  }

  async function resetPassword(email: string) {
    const client = getClient()

    try {
      const res = await client.post(`password-reset/`, {
        email: email,
      })

      return res.data as {
        detail: string
      }
    } catch (error) {
      throw error as AxiosError
    }
  }

  async function resendVerify(email: string) {
    const client = getClient()

    try {
      const res = await client.post('resend-verification/', {
        email: email,
      })

      if (res.status === 200) {
        toast('Verification email sent.', {
          type: 'success',
        })
      }

      return res.data
    } catch (error) {
      throw error as AxiosError
    }
  }

  async function logout() {
    removeToken()
    removeUserType()
    setUser(undefined)
    // Clear the last visited paths cache (FIR-5982)
    useLeftNavStore.getState().resetLastVisited()
    if (isAnalyticsEnabled) {
      posthog.reset()
    }
  }

  async function createUser(
    email: string,
    first_name: string,
    last_name: string,
    password1: string,
    password2: string
  ) {
    const client = getClient()

    try {
      const resp = await client.post(
        'auth/registration/',
        { email, first_name, last_name, password1, password2 },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )

      if (resp) {
        setIsVerifyStep(true)
      }
    } catch (error) {
      throw error as AxiosError
    }
  }

  useEffect(() => {
    const tryAuth = async () => {
      const token = getToken()

      if (!token) {
        setIsCheckingAuth(false)
        return
      }

      const client = getClient({ reloadOnAuthError: false })

      const [err, currUser] = await to(client.get('me/'))

      setUser(currUser?.data)

      // Keep stored user type in sync with server
      if (currUser?.data?.user_type) {
        setUserType(currUser.data.user_type)
      } else {
        removeUserType()
      }

      if (isAnalyticsEnabled) {
        if (!err && currUser.data) {
          Sentry.setUser({ email: currUser.data.email, id: currUser.data.id })
          posthog.identify(String(currUser.data.id), {
            email: currUser.data.email,
            name: `${currUser.data.first_name} ${currUser.data.last_name}`.trim(),
            is_staff: currUser.data.is_staff,
            company: currUser.data.company?.name,
          })
          // Only enable session recording in production for non-staff users
          if (
            import.meta.env.MODE === 'production' &&
            currUser.data.is_staff === false
          ) {
            posthog.startSessionRecording()
          }
        } else {
          Sentry.setUser(null)
          posthog.reset()
        }
      }

      setIsCheckingAuth(false)
    }

    void tryAuth()
  }, [])

  useEffect(() => {
    if (isAuthenticated) {
      setCookie(
        'fb_user_timezone',
        Intl.DateTimeFormat().resolvedOptions().timeZone
      )
    }
  }, [isAuthenticated])

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        login,
        createUser,
        getClient,
        logout,
        isCheckingAuth,
        isVerifyStep,
        resetPassword,
        resendVerify,
        resetPasswordConfirm,
        user,
        userType,
        isAdmin,
        isStandard,
        isLimited,
        isAllowed,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export interface AuthContextInterface {
  isAuthenticated: boolean
  createUser: (
    email: string,
    first_name: string,
    last_name: string,
    password1: string,
    password2: string
  ) => Promise<void>
  login: (email: string, password: string) => Promise<void>
  resetPassword: (email: string) => Promise<{
    detail: string
  }>
  logout: () => Promise<void>
  getClient: () => AxiosInstance
  isCheckingAuth: boolean
  isVerifyStep: boolean
  resendVerify: (email: string) => Promise<void>
  resetPasswordConfirm: (
    uid: string,
    token: string,
    new_password1: string,
    new_password2: string
  ) => Promise<{
    detail: string
  }>
  user: User | undefined
  userType: UserType
  isAdmin: boolean
  isStandard: boolean
  isLimited: boolean
  isAllowed: (requiredType: UserType) => boolean
}
